import './styles.scss';

import { faCheck, faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Button,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import React, { ReactNode, useEffect, useMemo, useState } from 'react';

import { OtcTraderSearchInput } from './search-input';

interface ISubListProperties {
  name: string;
  key: any;
  startIcon?: ReactNode;
  items: (IItemProperties | ISubListProperties)[];
}

interface IItemProperties {
  name: string;
  value: any;
  startIcon?: ReactNode;
  isDefaultValue?: boolean;
}

interface ISelectProperties {
  values: (IItemProperties | ISubListProperties)[];
  defaultValue: IItemProperties;
  onChange: (value: any) => void;
  className?: string;
  label?: string;
  customLabel?: ReactNode;
  disabled?: boolean;
  isSearchField?: boolean;
}

export function OtcTraderSelect(properties: ISelectProperties) {
  const {
    defaultValue,
    values,
    onChange,
    className,
    label,
    customLabel,
    disabled = false,
    isSearchField = false,
  } = properties;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [searchValue, setSearchValue] = useState('');
  const [selected, setSelected] = useState<IItemProperties>(defaultValue);
  const [openedListKey, setOpenedListKey] = useState('');

  useEffect(() => {
    setSelected(defaultValue);
  }, [defaultValue]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isList = (obj: any) => {
    return 'items' in obj;
  };

  const getColor = (level: number) => {
    const colors = ['#1D2F39', '#172730', '#102028'];

    if (level <= colors.length) {
      return colors[level];
    }

    return 'red';
  };

  const handleChange = (value: IItemProperties) => {
    setSelected(value);
    onChange(value);
    handleClose();
  };

  const filteredValues = useMemo(() => {
    const filterItems: any = (items: (IItemProperties | ISubListProperties)[], search: string) => {
      return items.filter((item) => {
        if (isList(item)) {
          const filteredSubItems = filterItems((item as ISubListProperties).items, search);
          return filteredSubItems.length > 0;
        } else {
          return (item as IItemProperties).name.toLowerCase().includes(search.toLowerCase());
        }
      });
    };

    return filterItems(values, searchValue);
  }, [values, searchValue]);

  const renderList = useMemo(() => {
    const renderItem = (item: IItemProperties, level: number) => {
      return (
        <MenuItem
          key={item.value}
          selected={selected.value === item.value}
          onClick={() => handleChange(item)}
          sx={{
            paddingLeft: `${(level + 1) * 16}px`,
            paddingY: '10px',
          }}
        >
          {item.startIcon && <ListItemIcon>{item.startIcon}</ListItemIcon>}
          <ListItemText disableTypography>{item.name}</ListItemText>
          <ListItemIcon className="selected-check-icon">
            {selected.value === item.value && <FontAwesomeIcon icon={faCheck} />}
          </ListItemIcon>
        </MenuItem>
      );
    };

    const renderSubList = (list: ISubListProperties, level: number) => {
      const isOpen = openedListKey.includes(list.key);

      const handleClickList = (key: string) => {
        let newKey = '';

        if (key === openedListKey) {
          const lastUndescore = openedListKey.lastIndexOf('_');

          if (lastUndescore > 0) {
            newKey = openedListKey.substring(lastUndescore + 1, openedListKey.length);
          }
        } else if (!openedListKey.includes(key)) {
          newKey = key;
        }

        setOpenedListKey(newKey);
      };

      return (
        <div key={list.key}>
          <ListItemButton
            onClick={() => handleClickList(list.key)}
            sx={{
              backgroundColor: getColor(level),
              paddingLeft: `${(level + 1) * 16}px`,
            }}
          >
            <ListItemText primary={list.name} />
            {isOpen ? <ExpandLess /> : <ExpandMore />}
          </ListItemButton>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {list.items.map((item) => {
                if (isList(item)) {
                  return renderSubList(item as ISubListProperties, level + 1);
                } else {
                  return renderItem(item as IItemProperties, level + 1);
                }
              })}
            </List>
          </Collapse>
        </div>
      );
    };

    return filteredValues.map((item: IItemProperties | ISubListProperties) => {
      if (!isList(item)) {
        return renderItem(item as IItemProperties, 0);
      } else {
        return renderSubList(item as ISubListProperties, 0);
      }
    });
  }, [filteredValues, onChange, openedListKey, selected]);

  return (
    <div className={`otc-trader-select-container ${className}`}>
      {!!label && <span className="label">{label}</span>}
      <Button
        onClick={handleClick}
        aria-controls={open ? 'menu' : undefined}
        aria-haspopup="true"
        disableRipple
        sx={{ borderRadius: 0 }}
        aria-expanded={open ? 'true' : undefined}
        variant="text"
        className={
          open
            ? 'otc-trader-selector-button otc-trader-selector-button-open '
            : ' otc-trader-selector-button'
        }
        endIcon={
          open ? <FontAwesomeIcon icon={faChevronUp} /> : <FontAwesomeIcon icon={faChevronDown} />
        }
      >
        <span
          className={`otc-trader-select-name ${
            selected.value === '' ? 'otc-trader-placeholder' : ''
          }`}
        >
          {' '}
          {customLabel || selected.name}
        </span>
      </Button>

      <Menu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        className="otc-trader-select-menu"
        sx={{
          '.MuiList-root': {
            display: 'flex',
            flexDirection: 'column',
            width: '220px',
            maxHeight: isSearchField ? '410px' : '',
            overflow: 'hidden',
          },
        }}
      >
        {isSearchField && (
          <OtcTraderSearchInput searchValue={searchValue} setSearchValue={setSearchValue} />
        )}
        <div className={`${isSearchField ? 'otc-trader-select-menu-list' : ''}`}>{renderList}</div>
      </Menu>
    </div>
  );
}
